import React, { ReactNode, useCallback, useRef, useState } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useBetterAuthConvexAuth } from '../../hooks/useAppAuth';
import { TERMS_TEXT } from '../../constants/termsText';
import { features } from '../../config/features';

interface TermsConditionsGateProps {
  children: ReactNode;
}

const TermsConditionsGate: React.FC<TermsConditionsGateProps> = ({ children }) => {
  const { requiresTermsConsent, isLoadingProfile, signOut } = useBetterAuthConvexAuth();
  const acceptTerms = useMutation(api.users.acceptTermsConditions);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Check if scrolled to within 20px of bottom
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 20) {
      setHasScrolledToBottom(true);
    }
  }, []);

  const handleAccept = async () => {
    setIsAccepting(true);
    setError('');
    try {
      await acceptTerms({});
    } catch (err: any) {
      setError(err.message || 'Failed to accept terms. Please try again.');
    } finally {
      setIsAccepting(false);
    }
  };

  const handleDecline = async () => {
    await signOut();
  };

  if (isLoadingProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!features.termsConsentEnabled || !requiresTermsConsent) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 py-8">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-6 sm:p-8">
        <div className="text-center mb-6">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-green-100 mb-4">
            <svg
              className="h-8 w-8 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Terms and Conditions
          </h2>
          <p className="text-gray-600">
            Please read and accept our terms and conditions to continue.
          </p>
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="h-72 sm:h-96 overflow-y-auto border border-gray-200 rounded-md p-4 mb-6 bg-gray-50 text-sm text-gray-700 whitespace-pre-line leading-relaxed"
        >
          {TERMS_TEXT}
        </div>

        {!hasScrolledToBottom && (
          <p className="text-sm text-gray-500 text-center mb-4">
            Please scroll to the bottom to continue
          </p>
        )}

        {error && (
          <p className="text-sm text-red-600 text-center mb-4">{error}</p>
        )}

        <div className="flex justify-center gap-3">
          <button
            onClick={handleDecline}
            className="px-6 py-3 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Decline & Sign Out
          </button>
          <button
            onClick={handleAccept}
            disabled={!hasScrolledToBottom || isAccepting}
            className="bg-[#16A34A] text-white px-6 py-3 rounded-md hover:bg-shade-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAccepting ? 'Accepting...' : 'I Accept'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsConditionsGate;
